<template>
  <q-card flat bordered>
    <div id="intercom-header" class="q-pa-md flex justify-between q-gutter-y-sm">
      <div>
        <div class="text-h6">Market Alerts</div>
        <div style="font-size: 12px">
          Identify recent trend changes between the subject property and selected comparables.
        </div>
      </div>
      <div id="intercom-market-alerts-actions">
        <q-select
          v-model="sensitivity"
          :options="sensitivityOptions"
          style="width: 150px"
          dense
          outlined
          label="Sensitivity"
          class="w-100"
        />
      </div>
    </div>
    <q-table
      id="intercom-market-alerts-content"
      bordered
      square
      dense
      flat
      :rows="rows"
      :columns="columns"
      :pagination="{
        rowsPerPage: 20
      }"
      :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
    >
      <template #no-data>
        <div class="q-mx-auto q-my-xl text-center">No alerts</div>
      </template>
      <template #header="props">
        <q-tr :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
          <q-th></q-th>
          <q-th></q-th>
          <q-th colspan="3" style="font-weight: bold; border-left: 1px solid #e0e0e0">
            30-Day Trend
          </q-th>
          <q-th colspan="3" style="font-weight: bold; border-left: 1px solid #e0e0e0">
            7-Day Trend
          </q-th>
          <q-th style="font-weight: bold; border-left: 1px solid #e0e0e0"></q-th>
        </q-tr>
        <q-tr :props="props" :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
          <q-th align="left">Metric</q-th>
          <q-th>Unit Type</q-th>
          <q-th style="font-weight: bold; border-left: 1px solid #e0e0e0">Subject</q-th>
          <q-th>Market</q-th>
          <q-th>Relative Change Δ</q-th>
          <q-th style="font-weight: bold; border-left: 1px solid #e0e0e0">Subject</q-th>
          <q-th>Market</q-th>
          <q-th>Relative Change Δ</q-th>
          <q-th style="font-weight: bold; border-left: 1px solid #e0e0e0">Explanation</q-th>
        </q-tr>
      </template>
      <template #body-cell-bedrooms="props">
        <q-td :props="props">
          <div :class="`hd-${props.row.bedrooms}-bed-bg hd-badge`">{{ props.row.bedrooms }}BR</div>
        </q-td>
      </template>
      <template #body-cell-subjectLongTerm="props">
        <q-td :props="props" style="border-left: 1px solid #e0e0e0">
          {{ props.row.formatFn(props.value) }}
        </q-td>
      </template>
      <template #body-cell-marketLongTerm="props">
        <q-td :props="props">
          {{ props.row.formatFn(props.value) }}
        </q-td>
      </template>
      <template #body-cell-subjectShortTerm="props">
        <q-td :props="props" style="border-left: 1px solid #e0e0e0">
          {{ props.row.formatFn(props.value) }}
          <span
            v-if="computeDelta(props.row.subjectLongTerm, props.row.subjectShortTerm) !== null"
            style="font-size: small"
            :class="`text-${
              (computeDelta(props.row.subjectLongTerm, props.row.subjectShortTerm) > 0 &&
                props.row.higherIsBetter) ||
              (computeDelta(props.row.subjectLongTerm, props.row.subjectShortTerm) < 0 &&
                !props.row.higherIsBetter)
                ? 'green-5'
                : 'red-5'
            }`"
          >
            ({{
              formatPercent(
                computeDelta(props.row.subjectLongTerm, props.row.subjectShortTerm),
                1,
                true
              )
            }})
          </span>
        </q-td>
      </template>
      <template #body-cell-marketShortTerm="props">
        <q-td :props="props">
          {{ props.row.formatFn(props.value) }}
          <span
            v-if="computeDelta(props.row.marketLongTerm, props.row.marketShortTerm) !== null"
            style="font-size: small"
            :class="`text-${
              (computeDelta(props.row.marketLongTerm, props.row.marketShortTerm) > 0 &&
                props.row.higherIsBetter) ||
              (computeDelta(props.row.marketLongTerm, props.row.marketShortTerm) < 0 &&
                !props.row.higherIsBetter)
                ? 'green-5'
                : 'red-5'
            }`"
          >
            ({{
              formatPercent(
                computeDelta(props.row.marketLongTerm, props.row.marketShortTerm),
                1,
                true
              )
            }})
          </span>
        </q-td>
      </template>
      <template #body-cell-longTermDeltaPerc="props">
        <q-td :props="props">
          <span
            :class="`text-${
              (props.value > 0 && props.row.higherIsBetter) ||
              (props.value < 0 && !props.row.higherIsBetter)
                ? 'green-5'
                : 'red-5'
            }`"
          >
            {{ formatPercent(props.value, 1, true) }}
          </span>
        </q-td>
      </template>
      <template #body-cell-shortTermDeltaPerc="props">
        <q-td :props="props">
          <span
            :class="`text-${
              (props.value > 0 && props.row.higherIsBetter) ||
              (props.value < 0 && !props.row.higherIsBetter)
                ? 'green-5'
                : 'red-5'
            }`"
          >
            {{ formatPercent(props.value, 1, true) }}
          </span>
        </q-td>
      </template>
      <template #body-cell-explanation="props">
        <q-td :props="props" align="left" style="border-left: 1px solid #e0e0e0; max-width: 350px">
          <div style="font-size: small; white-space: normal">
            {{ props.row.explanation }}
          </div>
        </q-td>
      </template>
    </q-table>
  </q-card>
</template>

<script lang="ts">
import { formatMoney, formatNumber, formatPercent } from '@/utils'
import { avg, type AvailabilityPeriod, type PropertyDetailsResponse } from 'common'
import { addDays, differenceInDays, eachDayOfInterval, format, parseISO } from 'date-fns'
import type { PropType } from 'vue'

function explanation(
  subject30d: number,
  market30d: number,
  subject7d: number,
  market7d: number,
  metric: string,
  bedrooms: number
): string {
  const subjectChange = subject30d === 0 ? null : ((subject7d - subject30d) / subject30d) * 100
  const marketChange = market30d === 0 ? null : ((market7d - market30d) / market30d) * 100

  const formatBedrooms = (bedrooms: number) => (bedrooms === 0 ? 'Studios' : `${bedrooms}BRs`)

  // Get subject trend description
  let subjectTrend = ''
  if (subjectChange === null) {
    subjectTrend = subject7d > 0 ? 'increased significantly' : 'decreased significantly'
  } else {
    const direction = subjectChange > 0 ? 'increased' : 'decreased'
    const magnitude = Math.abs(subjectChange) > 10 ? 'considerably' : 'slightly'
    subjectTrend = `${direction} ${magnitude} (${subjectChange.toFixed(1)}%)`
  }

  // Get market trend description
  let marketTrend = ''
  if (marketChange === null) {
    marketTrend = 'has insufficient data for comparison'
  } else if (Math.abs(marketChange) > 5) {
    marketTrend = `${marketChange > 0 ? 'increased' : 'decreased'} by ${Math.abs(marketChange).toFixed(1)}%`
  } else {
    marketTrend = 'remained relatively stable'
  }

  return `The ${metric} for ${formatBedrooms(bedrooms)} ${subjectTrend} over the last 7 days, while the overall market ${marketTrend} over the same period.`
}

interface MarketAlert {
  metric: Metric
  label: string
  bedrooms: number
  subjectShortTerm: number | null
  marketShortTerm: number | null
  shortTermDeltaPerc: number | null
  subjectLongTerm: number | null
  marketLongTerm: number | null
  longTermDeltaPerc: number | null
  formatFn: (val: number | null) => string
  higherIsBetter: boolean
  explanation: string
}

interface Aggregator {
  askingRent: number[]
  askingRentPsf: number[]
  concessionPercent: number[]
  daysOnMarket: number[]
  availabilities: number
  leasedUnits: number
}

const metricInfo: Record<
  Metric,
  {
    label: string
    aggFn: any
    higherIsBetter: boolean
    formatFn: (val: number | null) => string
    absoluteComparison: boolean
    explanationTitle: string
  }
> = {
  askingRent: {
    label: 'Asking Rent',
    aggFn: avg,
    higherIsBetter: true,
    formatFn: (val) => formatMoney(val, 0),
    explanationTitle: 'average Asking Rent',
    absoluteComparison: false
  },
  askingRentPsf: {
    label: 'Asking Rent Psf',
    aggFn: avg,
    higherIsBetter: true,
    formatFn: (val) => formatMoney(val, 1),
    absoluteComparison: false,
    explanationTitle: 'average Asking Rent PSF'
  },
  concessionPercent: {
    label: 'Concession %',
    aggFn: avg,
    higherIsBetter: false,
    formatFn: (val) => formatPercent(val, 1),
    absoluteComparison: true,
    explanationTitle: 'concessions'
  },
  daysOnMarket: {
    label: 'Days on Market',
    aggFn: avg,
    higherIsBetter: false,
    formatFn: (val) => formatNumber(val, false, 1),
    explanationTitle: 'average number of days on the market',
    absoluteComparison: false
  },
  availabilities: {
    label: 'Availabilities',
    aggFn: (val: number) => val,
    higherIsBetter: false,
    formatFn: (val) => formatNumber(val, false, 0),
    absoluteComparison: false,
    explanationTitle: 'number of available units'
  },
  leasedUnits: {
    label: 'Leased Units',
    aggFn: (val: number) => val,
    higherIsBetter: true,
    formatFn: (val) => formatNumber(val, false, 0),
    absoluteComparison: false,
    explanationTitle: 'number of leased units'
  }
}

type Metric = keyof Aggregator

export default {
  name: 'PropertyCompareMarketAlerts',
  props: {
    subjectDetail: {
      type: [Object, null] as PropType<PropertyDetailsResponse | null>,
      required: true
    },
    details: {
      type: Array<PropertyDetailsResponse>,
      required: true
    }
  },
  data() {
    return {
      sensitivity: { label: 'Medium', value: 0.05 },
      sensitivityOptions: [
        { label: 'Low', value: 0.1 },
        { label: 'Medium', value: 0.05 },
        { label: 'High', value: 0.025 }
      ]
    }
  },
  computed: {
    rows(): MarketAlert[] {
      const today = new Date()
      const lastWeek = addDays(today, -7)
      const lastMonth = addDays(today, -30)
      const todayStr = format(today, 'yyyy-MM-dd')
      const lastWeekStr = format(lastWeek, 'yyyy-MM-dd')
      const lastMonthStr = format(lastMonth, 'yyyy-MM-dd')

      const bedrooms = [0, 1, 2, 3, 4]

      // build aggregators for long and short term, and bedrooms
      const aggSubject: Record<number, { longTerm: Aggregator; shortTerm: Aggregator }> = {}
      const aggMarket: Record<number, { longTerm: Aggregator; shortTerm: Aggregator }> = {}

      // fill aggregators
      const allDetails = this.subjectDetail ? this.details.concat(this.subjectDetail) : this.details
      for (const prop of allDetails) {
        const relevantAgg = prop.id === this.subjectDetail?.id ? aggSubject : aggMarket
        for (const unit of prop.building_availability) {
          const bed = unit.bed
          if (bed === null) {
            continue
          }

          if (!(bed in relevantAgg)) {
            relevantAgg[bed] = {
              longTerm: {
                askingRent: [],
                askingRentPsf: [],
                concessionPercent: [],
                daysOnMarket: [],
                availabilities: 0,
                leasedUnits: 0
              },
              shortTerm: {
                askingRent: [],
                askingRentPsf: [],
                concessionPercent: [],
                daysOnMarket: [],
                availabilities: 0,
                leasedUnits: 0
              }
            }
          }

          const relevantPeriods: Map<number, AvailabilityPeriod> = new Map()
          for (const period of unit.availability_periods) {
            if (
              period.enter_market === null ||
              (period.enter_market && period.enter_market >= todayStr) ||
              (period.exit_market && period.exit_market < lastMonthStr)
            ) {
              continue
            }
            relevantPeriods.set(period.period_id, period)
            if (period.exit_market) {
              if (period.exit_market < lastWeekStr) {
                relevantAgg[bed].longTerm.leasedUnits += 1
              }
              if (period.exit_market < todayStr) {
                relevantAgg[bed].shortTerm.leasedUnits += 1
                relevantAgg[bed].longTerm.leasedUnits += 1
              }
            }
          }

          let shortTerm = false
          let longTerm = false

          for (const price of unit.history) {
            if (price.to_date < lastMonthStr) {
              continue
            }
            if (price.from_date > todayStr) {
              continue
            }
            const period = relevantPeriods.get(price.period_id)
            if (period === undefined) {
              continue
            }

            let initialDayOnMarket = differenceInDays(
              price.from_date,
              parseISO(period.enter_market!)
            )

            for (const day of eachDayOfInterval({
              start: parseISO(price.from_date),
              end: parseISO(price.to_date)
            })) {
              const dayStr = format(day, 'yyyy-MM-dd')
              if (dayStr >= lastMonthStr && dayStr < lastWeekStr) {
                if (!longTerm) {
                  longTerm = true
                  relevantAgg[bed].longTerm.availabilities += 1
                }
                if (price.price) {
                  relevantAgg[bed].longTerm.askingRent.push(price.price)
                  if (unit.sqft) {
                    relevantAgg[bed].longTerm.askingRentPsf.push(price.price / unit.sqft)
                  }
                  if (price.effective_price) {
                    relevantAgg[bed].longTerm.concessionPercent.push(
                      (price.price - price.effective_price) / price.price
                    )
                  }
                }
                relevantAgg[bed].longTerm.daysOnMarket.push(initialDayOnMarket)
              }

              if (dayStr >= lastWeekStr && dayStr < todayStr) {
                if (!shortTerm) {
                  shortTerm = true
                  relevantAgg[bed].shortTerm.availabilities += 1
                }
                if (price.price) {
                  relevantAgg[bed].shortTerm.askingRent.push(price.price)

                  if (unit.sqft) {
                    relevantAgg[bed].shortTerm.askingRentPsf.push(price.price / unit.sqft)
                  }
                  if (price.effective_price) {
                    relevantAgg[bed].shortTerm.concessionPercent.push(
                      (price.price - price.effective_price) / price.price
                    )
                  }
                }
                relevantAgg[bed].shortTerm.daysOnMarket.push(initialDayOnMarket)
              }
              initialDayOnMarket++
            }
          }
        }
      }

      // build results
      const results: MarketAlert[] = []
      for (const bed of bedrooms) {
        if (!aggSubject[bed] || !aggMarket[bed]) {
          continue
        }

        const { longTerm: longTermSubject, shortTerm: shortTermSubject } = aggSubject[bed]
        const { longTerm: longTermMarket, shortTerm: shortTermMarket } = aggMarket[bed]

        // aggregate metrics
        for (const metric of Object.keys(metricInfo) as Metric[]) {
          const { aggFn, label, higherIsBetter, formatFn, absoluteComparison, explanationTitle } =
            metricInfo[metric]
          const subjectLongTermVal = aggFn(longTermSubject[metric])
          const marketLongTermVal = aggFn(longTermMarket[metric])
          const subjectShortTermVal = aggFn(shortTermSubject[metric])
          const marketShortTermVal = aggFn(shortTermMarket[metric])

          if (
            subjectShortTermVal === null ||
            marketShortTermVal === null ||
            subjectLongTermVal === null ||
            marketLongTermVal === null
          ) {
            continue
          }

          let longTermDeltaPerc: number | null = null
          let shortTermDeltaPerc: number | null = null
          if (absoluteComparison) {
            longTermDeltaPerc = subjectLongTermVal - marketLongTermVal
            shortTermDeltaPerc = subjectShortTermVal - marketShortTermVal
          } else {
            longTermDeltaPerc = marketLongTermVal
              ? (subjectLongTermVal - marketLongTermVal) / marketLongTermVal
              : null
            shortTermDeltaPerc = marketShortTermVal
              ? (subjectShortTermVal - marketShortTermVal) / marketShortTermVal
              : null
          }

          if (shortTermDeltaPerc === null || longTermDeltaPerc === null) {
            continue
          }

          const deltaChange = shortTermDeltaPerc - longTermDeltaPerc

          if (Math.abs(deltaChange) < this.sensitivity.value * (absoluteComparison ? 0.5 : 1)) {
            continue
          }

          results.push({
            label,
            formatFn,
            higherIsBetter,
            metric,
            bedrooms: bed,
            subjectShortTerm: subjectShortTermVal,
            marketShortTerm: marketShortTermVal,
            shortTermDeltaPerc,
            subjectLongTerm: subjectLongTermVal,
            marketLongTerm: marketLongTermVal,
            longTermDeltaPerc,
            explanation: explanation(
              subjectLongTermVal,
              marketLongTermVal,
              subjectShortTermVal,
              marketShortTermVal,
              explanationTitle,
              bed
            )
          })
        }
      }
      return results
    },
    columns() {
      return [
        {
          name: 'metric',
          label: 'Metric',
          field: 'label',
          align: 'left',
          sortable: true
        },
        {
          name: 'bedrooms',
          label: 'Unit Type',
          field: 'bedrooms',
          align: 'center',
          sortable: true
        },

        {
          name: 'subjectLongTerm',
          label: "Subject's 30-Day Avg",
          field: (row: MarketAlert) => row.subjectLongTerm,
          align: 'center',
          sortable: true
        },
        {
          name: 'marketLongTerm',
          label: "Market's 30-Day Avg",
          field: (row: MarketAlert) => row.marketLongTerm,
          align: 'center',
          sortable: true
        },
        {
          name: 'longTermDeltaPerc',
          label: 'Relative',
          field: (row: MarketAlert) => row.longTermDeltaPerc,
          align: 'center',
          sortable: true
        },
        {
          name: 'subjectShortTerm',
          label: "Subject's 7-Day Avg",
          field: (row: MarketAlert) => row.subjectShortTerm,
          align: 'center',
          sortable: true
        },
        {
          name: 'marketShortTerm',
          label: "Market's 7-Day Avg",
          field: (row: MarketAlert) => row.marketShortTerm,
          align: 'center',
          sortable: true
        },
        {
          name: 'shortTermDeltaPerc',
          label: 'Relative',
          field: (row: MarketAlert) => row.shortTermDeltaPerc,
          align: 'center',
          sortable: true
        },
        {
          name: 'explanation',
          label: 'Explanation',
          field: 'explanation',
          align: 'left'
        }
      ]
    }
  },
  methods: {
    formatPercent,
    computeDelta(from: number, to: number): number | null {
      if (from === 0) {
        return null
      }
      return (to - from) / from
    }
  }
}
</script>
